import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Typography, Button, Tag, Divider, Alert, Table } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  BugOutlined,
  SettingOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  releaseNotesContentMap,
  ReleaseNoteContent,
  Feature,
} from "./releaseNotesContent";
import "./index.scss";

const { Title, Text, Paragraph } = Typography;

const ReleaseNotesDetail: React.FC = () => {
  const { version } = useParams<{ version: string }>();
  const navigate = useNavigate();

  // Get content for the requested version
  const content: ReleaseNoteContent | undefined = version
    ? releaseNotesContentMap[version]
    : undefined;

  // If version not found, show error
  if (!content) {
    return (
      <div className="release-notes-detail-container">
        <div className="release-notes-detail-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/resources?tab=release-notes")}
          >
            Back to Release Notes
          </Button>
        </div>
        <Alert
          type="info"
          message="Release Notes Not Available"
          description={`Detailed web release notes for version ${version} are not available. Please check the PDF documentation if available.`}
          showIcon
        />
      </div>
    );
  }

  const terminologyColumns = [
    {
      title: "Old Term",
      dataIndex: "old",
      key: "old",
    },
    {
      title: "New Term",
      dataIndex: "new",
      key: "new",
    },
  ];

  const getPortalTagColor = (portal: string) => {
    switch (portal) {
      case "Admin Portal":
        return "blue";
      case "Candidate Portal":
        return "green";
      case "Background":
        return "default";
      case "Both Portals":
        return "purple";
      default:
        return "default";
    }
  };

  const renderFeatureCard = (feature: Feature) => (
    <div key={feature.id} className="feature-card">
      <div className="feature-card-header">
        <div className="feature-icon">{feature.icon}</div>
        <div className="feature-header-content">
          <Title level={4} className="feature-title">
            {feature.id}. {feature.title}
          </Title>
          <Tag color={getPortalTagColor(feature.portal)}>{feature.portal}</Tag>
        </div>
      </div>

      <Paragraph className="feature-description">{feature.description}</Paragraph>

      {feature.benefits && (
        <ul className="feature-list">
          {feature.benefits.map((benefit, idx) => (
            <li key={idx}>{benefit}</li>
          ))}
        </ul>
      )}

      {feature.subFeatures && (
        <div className="sub-features">
          {feature.subFeatures.map((sub, idx) => (
            <div key={idx} className="sub-feature">
              <Text strong className="sub-feature-title">
                {sub.title}
              </Text>
              <ul className="feature-list">
                {sub.items.map((item, itemIdx) => (
                  <li key={itemIdx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {feature.howToAccess && (
        <div className="how-to-access">
          <Text type="secondary">
            <strong>How to access:</strong> {feature.howToAccess}
          </Text>
        </div>
      )}

      {feature.note && (
        <Alert
          type="info"
          message={feature.note}
          className="feature-note"
          showIcon
        />
      )}
    </div>
  );

  return (
    <div className="release-notes-detail-container">
      {/* Header */}
      <div className="release-notes-detail-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/resources?tab=release-notes")}
        >
          Back to Release Notes
        </Button>
      </div>

      {/* Hero Section */}
      <div className="release-notes-hero">
        <div className="hero-content">
          <div className="hero-badge">
            <Tag color="#6366f1">RELEASE NOTES</Tag>
          </div>
          <Title level={1} className="hero-title">
            QualifyMe v{content.version}
          </Title>
          <div className="hero-meta">
            <Text className="hero-date">
              <strong>Release Date:</strong> {content.releaseDate}
            </Text>
            <Text className="hero-previous">
              <strong>Previous Version:</strong> {content.previousVersion}
            </Text>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <section className="release-section">
        <Title level={2} className="section-title">
          <RocketOutlined className="section-icon" />
          Overview
        </Title>
        <Paragraph className="overview-text">{content.overview}</Paragraph>
      </section>

      <Divider />

      {/* What's New Section */}
      <section className="release-section">
        <Title level={2} className="section-title">
          <CheckCircleOutlined className="section-icon" />
          What's New
        </Title>

        <div className="features-grid">
          {content.features.map(renderFeatureCard)}
        </div>
      </section>

      <Divider />

      {/* Bug Fixes Section */}
      <section className="release-section">
        <Title level={2} className="section-title">
          <BugOutlined className="section-icon" />
          Bug Fixes
        </Title>
        <ul className="bug-fixes-list">
          {content.bugFixes.map((fix, idx) => (
            <li key={idx}>
              <CheckCircleOutlined className="fix-icon" />
              {fix}
            </li>
          ))}
        </ul>
      </section>

      {/* Terminology Changes Section (if exists) */}
      {content.terminologyChanges && content.terminologyChanges.length > 0 && (
        <>
          <Divider />
          <section className="release-section">
            <Title level={2} className="section-title">
              <ExclamationCircleOutlined className="section-icon" />
              Terminology Changes
            </Title>
            <Paragraph>
              These changes are reflected throughout the platform including
              emails, notifications, and UI labels.
            </Paragraph>
            <Table
              dataSource={content.terminologyChanges}
              columns={terminologyColumns}
              pagination={false}
              rowKey="old"
              className="terminology-table"
            />
          </section>
        </>
      )}

      {/* Known Issues Section (if exists) */}
      {content.knownIssues && content.knownIssues.length > 0 && (
        <>
          <Divider />
          <section className="release-section">
            <Title level={2} className="section-title">
              <WarningOutlined className="section-icon" />
              Known Issues
            </Title>
            <ul className="known-issues-list">
              {content.knownIssues.map((issue, idx) => (
                <li key={idx}>
                  <WarningOutlined className="issue-icon" />
                  {issue}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      {/* Deprecations Section (if exists) */}
      {content.deprecations && content.deprecations.length > 0 && (
        <>
          <Divider />
          <section className="release-section">
            <Title level={2} className="section-title">
              <ExclamationCircleOutlined className="section-icon" />
              Deprecations
            </Title>
            <ul className="deprecations-list">
              {content.deprecations.map((deprecation, idx) => (
                <li key={idx}>{deprecation}</li>
              ))}
            </ul>
          </section>
        </>
      )}

      {/* System Requirements Section (if exists) */}
      {content.systemRequirements && (
        <>
          <Divider />
          <section className="release-section">
            <Title level={2} className="section-title">
              <SettingOutlined className="section-icon" />
              System Requirements
            </Title>

            <div className="requirements-grid">
              <div className="requirement-card">
                <Title level={5}>Supported Browsers</Title>
                <ul>
                  {content.systemRequirements.browsers.map((browser, idx) => (
                    <li key={idx}>{browser}</li>
                  ))}
                </ul>
              </div>

              <div className="requirement-card">
                <Title level={5}>For AI Assessments</Title>
                <ul>
                  {content.systemRequirements.aiAssessments.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <div className="release-notes-footer">
        <Divider />
        <div className="footer-content">
          <Title level={4}>Need Help?</Title>
          <Paragraph>For questions or issues with this release:</Paragraph>
          <ul>
            <li>
              <strong>Email:</strong> support@qualifyme.ai
            </li>
            <li>
              <strong>In-App:</strong> Resources → Contact Support
            </li>
            <li>
              <strong>Documentation:</strong> Resources → FAQ
            </li>
          </ul>
          <Paragraph className="thank-you">
            Thank you for using QualifyMe. We're committed to continuously
            improving your hiring experience.
          </Paragraph>
        </div>
      </div>
    </div>
  );
};

export default ReleaseNotesDetail;
